import { CHART_ELEMENTS, getConfig } from './types';
import { elementColor } from '../../../utils/elementColors';
import { renderElementIcon } from '../../components/ItemIcon';
import { buildElementTooltip } from '../../components/ElementTooltip';
import { Tooltip } from '../../components/Tooltip';
import { formatElement } from '../../components/GameTerms';

interface MatchupCounts { weak: number; resist: number; selfResist: boolean; }

function computeCounts(element: string, chart: Record<string, Record<string, number>>): MatchupCounts {
  let weak = 0;
  let resist = 0;
  for (const atk of CHART_ELEMENTS) {
    const v = chart[atk]?.[element] ?? 0;
    if (v < 0) weak++;
    else if (v > 0 && atk !== element) resist++;
  }
  const selfResist = (chart[element]?.[element] ?? 0) > 0;
  return { weak, resist, selfResist };
}

function renderElementChip(element: string, counts: MatchupCounts): string {
  const color = elementColor(element);
  const name = formatElement(element);
  const selfMark = counts.selfResist ? '<span class="el-chip-self" title="Self-resist">●</span>' : '';
  return `
    <button class="el-chip" data-el-chip="${element}" style="--el-color:${color}">
      ${renderElementIcon(element, 'sm')}
      <span class="el-chip-name" style="color:${color}">${name}</span>
      <span class="el-chip-counts">
        <span class="el-chip-weak" title="${counts.weak} types hit this for super-effective damage">W ${counts.weak}</span>
        <span class="el-chip-resist" title="${counts.resist} types this resists">R ${counts.resist}${selfMark ? ' +S' : ''}</span>
      </span>
    </button>
  `;
}

function matchupLabel(atk: string, def: string, val: number): string {
  const aName = formatElement(atk);
  const dName = formatElement(def);
  if (val < 0) return `${aName} → ${dName}: ${dName} takes ${val}% (weak)`;
  if (val > 0) return atk === def
    ? `${aName} resists itself (+${val}%)`
    : `${aName} → ${dName}: ${dName} resists +${val}%`;
  return `${aName} → ${dName}: neutral`;
}

export function renderElements(content: HTMLElement): void {
  const config = getConfig();
  if (!config?.elementChart) { content.innerHTML = '<p>Loading...</p>'; return; }

  const chart = config.elementChart;
  const cols = CHART_ELEMENTS.length + 1;

  const chips = CHART_ELEMENTS
    .map(el => renderElementChip(el, computeCounts(el, chart)))
    .join('');

  let gridHtml = '';
  gridHtml += `<div class="codex-element-cell corner"><span class="corner-atk">ATK ↓</span><span class="corner-def">DEF →</span></div>`;
  for (const def of CHART_ELEMENTS) {
    const c = elementColor(def);
    gridHtml += `<div class="codex-element-cell header" data-el-header="${def}" style="--el-color:${c}">${formatElement(def).slice(0, 4)}</div>`;
  }
  for (const atk of CHART_ELEMENTS) {
    const c = elementColor(atk);
    gridHtml += `<div class="codex-element-cell row-header" data-el-header="${atk}" style="--el-color:${c}">${formatElement(atk).slice(0, 4)}</div>`;
    for (const def of CHART_ELEMENTS) {
      const val = chart[atk]?.[def] ?? 0;
      let cls = 'neutral';
      let display = '·';
      if (val < 0) { cls = 'weak'; display = `${val}`; }
      else if (val > 0) { cls = atk === def ? 'self-resist' : 'resist'; display = `+${val}`; }
      const tip = matchupLabel(atk, def, val);
      gridHtml += `<div class="codex-element-cell ${cls}" data-atk="${atk}" data-def="${def}" title="${tip}">${display}</div>`;
    }
  }

  content.innerHTML = `
    <h2 class="codex-section-title">Elements</h2>
    <p class="codex-section-subtitle">Hover any element for its full matchup. Rows = attacker, Columns = defender on the chart below.</p>

    <div class="el-chip-strip">
      ${chips}
    </div>

    <div class="codex-element-chart">
      <div class="codex-element-grid" style="grid-template-columns: repeat(${cols}, auto);">
        ${gridHtml}
      </div>
    </div>
    <div class="codex-chart-legend">
      <div class="codex-legend-item"><div class="codex-legend-swatch" style="background:rgba(255,68,68,0.22)"></div> Weakness (-35)</div>
      <div class="codex-legend-item"><div class="codex-legend-swatch" style="background:rgba(68,170,255,0.18)"></div> Resist (+35)</div>
      <div class="codex-legend-item"><div class="codex-legend-swatch" style="background:rgba(68,170,255,0.10)"></div> Self-Resist</div>
    </div>
    <div class="codex-chart-note">
      <strong>Formula:</strong> effectiveRes = chartBase + templateMod + gearRes − pen<br/>
      Damage multiplier = clamp(1 − effectiveRes/100, 0.25, 1.75).<br/>
      Dual-typed defenders sum both types' values.
    </div>
  `;

  // Wire matchup tooltips on chips and chart headers — both reuse the canonical
  // ElementTooltip so the player sees the same data everywhere.
  content.querySelectorAll<HTMLElement>('[data-el-chip]').forEach(el => {
    const id = el.dataset.elChip!;
    Tooltip.attach(el, () => buildElementTooltip(id));
  });
  content.querySelectorAll<HTMLElement>('[data-el-header]').forEach(el => {
    const id = el.dataset.elHeader!;
    Tooltip.attach(el, () => buildElementTooltip(id));
  });

  // Chart row/column highlight on cell hover.
  content.querySelectorAll('.codex-element-cell[data-atk]').forEach(cell => {
    cell.addEventListener('mouseenter', () => {
      const atk = (cell as HTMLElement).dataset.atk!;
      const def = (cell as HTMLElement).dataset.def!;
      content.querySelectorAll('.codex-element-cell[data-atk]').forEach(c => {
        const elx = c as HTMLElement;
        elx.classList.toggle('highlight', elx.dataset.atk === atk || elx.dataset.def === def);
      });
      content.querySelectorAll<HTMLElement>('.codex-element-cell.header').forEach(h => {
        h.classList.toggle('highlight', h.dataset.elHeader === def);
      });
      content.querySelectorAll<HTMLElement>('.codex-element-cell.row-header').forEach(h => {
        h.classList.toggle('highlight', h.dataset.elHeader === atk);
      });
    });
    cell.addEventListener('mouseleave', () => {
      content.querySelectorAll('.codex-element-cell').forEach(c => c.classList.remove('highlight'));
    });
  });
}
